import beTwMerge from '@/utils/beTwMerge';
import { formatRichText, getAnchorId, getComponentMargin, isRichTextEmpty } from '@/utils/builderUtils';
import ResponsiveImage, { ResponsiveImageSet } from '@/utils/ResponsiveImage';
import Button from '@/components/homepage/Button/Button';
import DropdownButton from '@/components/homepage/DropdownButton';
import { subHeadersStyles } from '@/consts/builder';
import useMediaQuery from '@/hooks/useMediaQuery';
import { ActionsType } from '@/store/types/IBuilder';
import RichText from '@/components/RichText/RichText';

type CustomerServiceCopyBlockFullProps = {
  imgSrc?: string;
  mobileImgSrc?: string;
  imgAlt?: string;
  imgLink?: string;
  header?: string;
  headerTag: 'h1' | 'h2';
  description: string;
  textPosition: string;
  actions?: ActionsType[];
  margin: boolean;
  anchorLinkID?: string;
};

export default function CustomerServiceCopyBlockFull({
  imgSrc,
  mobileImgSrc,
  imgAlt,
  imgLink,
  header,
  headerTag,
  description,
  textPosition,
  actions = [],
  margin,
  anchorLinkID,
}: CustomerServiceCopyBlockFullProps) {
  const isCenter = textPosition === 'center';
  const isTablet = useMediaQuery('(min-width: 768px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isShowImage = (isDesktop && imgSrc) || (!isDesktop && mobileImgSrc);
  const slicedActions: ActionsType[] = actions.slice(0, 1).map(action => ({
    ...action,
    color: (action.color as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
    mobileColor: (action.mobileColor as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
  }));
  const HeaderTag = headerTag || 'h1';

  const renderImage = () => {
    let className = 'tw-block lg:tw-hidden tw-w-full tw-h-full tw-object-cover';

    let imgSrcs: ResponsiveImageSet = [
      {
        src: mobileImgSrc || '',
        size: { width: 358, height: 229, aspectRatio: 358 / 229 },
        breakpoint: 320,
      },
    ];
    if (isDesktop) {
      imgSrcs = [
        {
          src: imgSrc || '',
          size: { width: 967, height: 569, aspectRatio: 967 / 569 },
          breakpoint: 1023,
        },
      ];
      className = 'lg:tw-block tw-hidden tw-w-full tw-h-full tw-object-cover';
    }
    const image = <ResponsiveImage className={className} sources={imgSrcs} alt={imgAlt} />;
    if (imgLink)
      return (
        <a href={imgLink} className="tw-block tw-w-full tw-object-cover">
          {image}
        </a>
      );
    return image;
  };

  return (
    <div
      className={`tw-mx-4 lg:tw-mx-0 ${margin ? 'tw-mb-14' : ''}`}
      style={{
        scrollMarginTop: isTablet ? 0 : 132,
      }}
      {...getAnchorId(anchorLinkID)}
    >
      <div className="tw-grid tw-col-span-full lg:tw-grid-cols-12 tw-w-full">
        <div className={`tw-col-span-full tw-flex tw-flex-col ${isCenter ? 'tw-text-center' : 'tw-text-start'}`}>
          {header && (
            <HeaderTag className={beTwMerge('tw-mb-4 tw-mt-0', subHeadersStyles[headerTag])}>{header}</HeaderTag>
          )}
          {!isRichTextEmpty(description) && (
            <RichText
              className={`[&_ul]:tw-m-0 [&_ol]:tw-m-0 ${isCenter ? '[&_ul]:tw-list-inside [&_ol]:tw-list-inside [&_ul]:tw-pl-0 [&_ol]:tw-pl-0' : ''} tw-cursor-default ${imgSrc ? 'tw-mb-8 bs-tablet:tw-mb-4' : ''}`}
              content={formatRichText(description)}
            />
          )}
          {isShowImage && <div> {renderImage()}</div>}

          {slicedActions.length > 0 && (
            <div
              className={`tw-mt-[32px] md:tw-flex tw-w-full  ${isCenter ? 'md:tw-justify-center' : 'md:tw-justify-start'}`}
            >
              {slicedActions.map((action, i) => {
                const { color, isDropdownButton, label, mobileColor, mobileVariant, variant } = action;

                if (isDropdownButton && action.actions?.length) {
                  const { actions: subActions } = action;
                  return (
                    <DropdownButton
                      key={i}
                      label={label}
                      options={subActions}
                      color={isDesktop ? color : mobileColor}
                      type={isDesktop ? variant : mobileVariant}
                      className="md:tw-max-w-[146px] tw-basis-[calc(50%-8px)] tw-w-full tw-text-center tw-max-w-none [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis"
                    />
                  );
                }
                const { onClick, href } = action;
                return (
                  <Button
                    className="md:tw-max-w-[146px] tw-basis-[calc(50%-8px)] tw-max-w-none tw-w-full tw-text-center tw-whitespace-nowrap tw-text-ellipsis md:tw-overflow-hidden"
                    key={i}
                    label={label}
                    color={isDesktop ? color : mobileColor}
                    type={isDesktop ? variant : mobileVariant}
                    href={href}
                    onClick={onClick}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
